import { Collapse, ThemeProvider } from '@mui/material';
import {
  ActionMeta,
  Builder,
  BuilderProps,
  Config,
  ImmutableTree,
  JsonTree,
  Utils as QbUtils,
  Query,
  RuleProperties,
} from '@react-awesome-query-builder/mui';
import '@react-awesome-query-builder/mui/css/styles.css';
// For more compact styles:
// import '@react-awesome-query-builder/mui/css/compact_styles.css';
import { internalAgSystemsTheme } from '@australiangreens/ag-internal-components';
import { Dispatch, SetStateAction, useCallback } from 'react';

import { SearchTreeNode } from '@/types/searchTreeTypes';
import {
  mapQbRulePropertiesToSearchFilter,
  MapQbRulePropertiesToSearchFilterOptions,
} from './qbRulePropertiesMapping';
import { FilterMappers, MappingError } from './qbRulePropertiesMapping/FilterMappers';
import QueryBuilderDebugInfo from './QueryBuilderDebugInfo';
import './styles.css';
import { mapQbJsonTreeToSearchTree } from './treeMapping';

// This is a particular state in SearchTreeBuilder that unfortunately has to be
// lifted out of that into AdvancedSearchListCreatorPage for LIST-891 and
// LIST-912.

export type SearchTreeBuilderState = {
  tree: ImmutableTree | undefined;
  config: Config;
};

export type SearchTreeChangeHandler = (
  searchTree: SearchTreeNode | undefined,
  isIncomplete: boolean,
  actionMeta?: ActionMeta
) => void;

type SearchTreeBuilderProps = {
  onSearchTreeChange: SearchTreeChangeHandler;
  searchTreeBuilderState: SearchTreeBuilderState;
  setSearchTreeBuilderState: Dispatch<SetStateAction<SearchTreeBuilderState>>;
  debug?: boolean;
  /** Set the datatest-id on the root element for using reactdom's getByTestId()
   * function */
  'data-testid'?: string;
};

export const mapToSearchTree = (
  immutableTree: ImmutableTree,
  config: Config,
  options: MapQbRulePropertiesToSearchFilterOptions
) => {
  const immutableTreeFixed = QbUtils.sanitizeTree(immutableTree, config).fixedTree;
  const jsonTree = QbUtils.getTree(immutableTreeFixed);
  const newSearchTree = mapQbJsonTreeToSearchTree(
    jsonTree,
    mapQbRulePropertiesToSearchFilter,
    options
  );
  return Object.keys(newSearchTree).length === 0 ? undefined : newSearchTree;
};

export function checkIsSearchTreeIncomplete(immuatableTree: ImmutableTree | undefined): boolean {
  if (immuatableTree === undefined) {
    return true;
  }
  const tree = QbUtils.getTree(immuatableTree);

  return checkIsJsonTreeIncomplete(tree);
}

function checkIsRuleIncomplete(properties: RuleProperties) {
  if (typeof properties.field !== 'string') return true;
  return (
    properties.field === null ||
    properties.value.length === 0 ||
    properties.value[0] === undefined ||
    // Some of our widgets populate an errors array if there is an invalid
    // combination of values or something else and the user is being shown
    // a message about it in the widget. At the moment we don't care what
    // those errors are, just knowing there are any means the tree should be
    // considered incomplete.
    (
      ((properties.value[0].errors ?? []) as MappingError[]).filter(
        ({ level }) => level === 'error'
      ) ?? []
    ).length > 0 ||
    FilterMappers[properties.field].checkIsIncomplete(properties)
  );
  //
}

function checkIsJsonTreeIncomplete(tree: JsonTree): boolean {
  if (tree.type !== 'group' || !Array.isArray(tree.children1)) return false;
  return tree.children1?.some((node) =>
    node.type === 'rule'
      ? checkIsRuleIncomplete(node.properties)
      : node.type === 'group'
        ? checkIsJsonTreeIncomplete(node)
        : false
  );
}

export default function SearchTreeBuilder({
  onSearchTreeChange,
  searchTreeBuilderState,
  setSearchTreeBuilderState,
  debug = false,
  'data-testid': dataTestId,
}: SearchTreeBuilderProps) {
  const config = searchTreeBuilderState.config;

  const onChange = useCallback(
    (immutableTree: ImmutableTree, newConfig: Config, actionMeta?: ActionMeta) => {
      const newPossibleSearchTree = mapToSearchTree(immutableTree, config, {});

      const isSearchTreeIncomplete = checkIsSearchTreeIncomplete(immutableTree);
      setSearchTreeBuilderState((prevState) => ({
        ...prevState,
        tree: immutableTree,
        config: newConfig,
      }));
      onSearchTreeChange(newPossibleSearchTree, isSearchTreeIncomplete, actionMeta);
    },
    [setSearchTreeBuilderState, onSearchTreeChange, config]
  );

  const renderBuilder = useCallback(
    (props: BuilderProps) => (
      <ThemeProvider theme={internalAgSystemsTheme}>
        <div className="query-builder-container">
          <div className="query-builder" data-testid={dataTestId}>
            <Builder {...props} />
          </div>
        </div>
      </ThemeProvider>
    ),
    [dataTestId]
  );

  return (
    <>
      {searchTreeBuilderState.tree && (
        <Query
          {...config}
          value={searchTreeBuilderState.tree}
          onChange={onChange}
          renderBuilder={renderBuilder}
        />
      )}

      <Collapse in={debug}>
        {searchTreeBuilderState.tree && (
          <QueryBuilderDebugInfo
            internalTree={searchTreeBuilderState.tree}
            config={searchTreeBuilderState.config}
          />
        )}
      </Collapse>
    </>
  );
}
